// src/services/auth.ts
// ══════════════════════════════════════════════

import api from './api';
import { User } from '../types';

export const authService = {
  login: async (email: string, password: string) => {
    try {
      // console.log('🔐 Attempting login with:', email);

      const res = await api.post('/login', { email, password });
      // console.log('✅ Login response received:', res);

      if (!res.data) {
        throw new Error('Empty response from server');
      }

      const responseData = res.data;
      // console.log('📦 Response data:', responseData);

      // Extract user and token from response
      const user = responseData.data?.user;
      const token = responseData.data?.token;
      const capabilities = responseData.data?.capabilities || [];

      if (!user || !token) {
        console.error('❌ Missing user or token in response', { user, token });
        throw new Error('Invalid response format: missing user or token');
      }

      /*
      console.log('✅ Login successful for user:', user.email);
      console.log('🔑 Capabilities:', capabilities);
      */

      return { user, token, capabilities } as { user: User; token: string; capabilities: string[] };
    } catch (err: any) {
      console.error('❌ Login error:', err);
      if (err.response?.data) {
        console.error('📋 Response data:', err.response.data);
      }
      throw err;
    }
  },

  logout: async () => {
    await api.post('/logout');
  },

  getUser: async () => {
    const res = await api.get('/user');
    const data = res.data.data || res.data;

    // Also extract capabilities if present
    const capabilities = data.capabilities || res.data.capabilities || [];

    // Store capabilities
    if (capabilities.length > 0) {
      localStorage.setItem('capabilities', JSON.stringify(capabilities));
    }

    // Return user without capabilities field (clean user object)
    const { capabilities: _, ...userData } = data;
    return userData as User;
  },

  forgotPassword: async (email: string) => {
    const res = await api.post('/forgot-password', { email });
    return res.data;
  },

  resetPassword: async (data: { token: string; email: string; password: string; password_confirmation: string }) => {
    const res = await api.post('/reset-password', data);
    return res.data;
  },

  updateProfile: async (data: {
    name?: string;
    phone?: string;
    avatar?: File;
  }): Promise<User> => {
    const formData = new FormData();
    if (data.name) formData.append('name', data.name);
    if (data.phone !== undefined) formData.append('phone', data.phone);
    if (data.avatar) formData.append('avatar', data.avatar);

    const res = await api.post('/user/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const updatedUser = res.data.data || res.data;
    localStorage.setItem('user', JSON.stringify(updatedUser));

    return updatedUser as User;
  },

  changePassword: async (data: { current_password: string; password: string; password_confirmation: string }) => {
    const res = await api.put('/user/password', data);
    return res.data;
  },

  getSettings: async () => {
    const res = await api.get('/user/settings');
    return res.data.data as {
      email_notifications: boolean;
      browser_notifications: boolean;
      language: string;
      timezone: string;
    };
  },

  updateSettings: async (data: {
    email_notifications?: boolean;
    browser_notifications?: boolean;
    language?: string;
    timezone?: string;
  }) => {
    const res = await api.put('/user/settings', data);
    return res.data;
  },
};
